// src/chatServices/XMPPservice.js
/* eslint-disable no-unused-expressions */
import { client, xml } from "@xmpp/client";
import { OPENFIRE_WS_URL, OPENFIRE_WSS_URL } from "../constants/ApiPath";
import moment from "moment";
import {
  appendMessaageToCoversationData,
  getChatUsersListDataDjango,
  setChatUsers,
  setSessionAddress,
} from "../appRedux/actions/Chat";
import { transformInternalChat } from "../routes/ProjectManagementTool/PartnerChat/PartnerChat";
import Emitter, { CHAT_CONNECTION_RETRIES } from "../services/event-emitter";
import { notification } from "antd";
// const debug = require("@xmpp/debug");

export const XMPP_NOTIFICATION_MESSAGE = "XMPP_NOTIFICATION_MESSAGE";

export const timeout = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

let store;
export const injectStoreToXMPPService = (_store) => {
  store = _store;
};

export let xmpp;
export let xmppUser;
export let isXMPPConnected = false;
let retryCount = 0;
const MAX_RETRIES = 10;

export const initializeXmpp = async (
  user,
  pass,
  domain = "chat.orion22.com",
  { setXMPP }
) => {
  if (xmpp?.status === "online") {
    return;
  }

  try {
    xmppUser = user;
    xmpp = await client({
      service:OPENFIRE_WS_URL, // WebSocket endpoint
      // service: OPENFIRE_WSS_URL, // WebSocket endpoint
      domain: domain, // XMPP server domain
      username: user,
      password: pass,
    });

    // reconnect configuration
    if (xmpp?.reconnect) {
      xmpp.reconnect.maxDelay = 10000; // 10 seconds max delay
      xmpp.reconnect.delay = 2000; // 2 second between retries
      xmpp.reconnect.initialDelay = 1000; // Start with 1 second
      xmpp.reconnect.retries = MAX_RETRIES; // Maximum 10 retries
    }

    // Uncomment below line if want to debug errors, warnings
    // debug(xmpp, true);

    xmpp?.on("error", async (err) => {
      // console.warn("XMPP Error:", err.toString());
      if (err.toString()?.includes("WebSocket ECONNERROR")) {
        retryCount++;
        // console.warn(`Connection attempt ${retryCount} of ${MAX_RETRIES}`);
        Emitter.emit(CHAT_CONNECTION_RETRIES, retryCount);
        notification.open({
          key: XMPP_NOTIFICATION_MESSAGE,
          message: "Connecting to Chat",
          description: `Retrying connection to Chat. Attempt ${retryCount} of ${MAX_RETRIES}`,
          duration: 5,
        });

        if (retryCount >= MAX_RETRIES) {
          console.warn("Maximum retry attempts reached. Stopping Connection.");
          await removeXMPP(() => {
            setXMPP(null);
          });
        }
      }
    });

    xmpp?.on("online", async (address) => {
      // console.log("Online as", address);
      // fetchUsers();
      notification.success({
        message: "Chat Connected successfully",
      });
      isXMPPConnected = true;
      retryCount = 0; // Reset retry count on successful connection
      const presence = xml("presence");
      await xmpp?.send(presence);
      if (store) {
        store?.dispatch(setSessionAddress(address));
        // await timeout(1000);
        // store?.dispatch(prepareChatUsersList());
        // fetchUsers()
        //   .then(roster => {
        //     // console.log('ROSTER', roster);
        //     store?.dispatch(setRosterList(roster));
        //     let prepDeb = setTimeout(() => {
        //       store?.dispatch(prepareChatUsersList());
        //       clearTimeout(prepDeb);
        //     }, 500);
        //   })
        //   .catch(console.warn);
      }
    });

    xmpp?.on("offline", async () => {
      notification.error({
        message: "Chat Disconnected",
      });
      if (store) {
        store?.dispatch(setSessionAddress(null));
      }
    });

    xmpp?.on("stanza", (stanza) => {
      if (stanza.is("message") && stanza.getChild("body")) {
        const from = stanza.attrs.from;
        const to = stanza.attrs.to;
        const body = stanza.getChildText("body");
        // fallback timestamp
        const fallbackTimestamp = new Date().toISOString();

        if (store) {
          const selectedUser = store?.getState().chat?.selectedUser;
          if (selectedUser?.receiver_uuid) {
            if (from?.includes(selectedUser?.receiver_uuid)) {
              store?.dispatch(
                appendMessaageToCoversationData({
                  body: body,
                  from: from,
                  isRead: true,
                  timestamp: fallbackTimestamp,
                  to: to,
                  messageTimeStamp: moment(fallbackTimestamp).valueOf(),
                  formattedDate:
                    moment(fallbackTimestamp).format("MMM DD, YYYY"),
                  formattedTime: moment(fallbackTimestamp).format("hh:mm a"),
                })
              );
            }
          }
          store?.dispatch(
            getChatUsersListDataDjango((res) => {
              const transformedData = transformInternalChat(res?.data);
              store?.dispatch(setChatUsers(transformedData));

              let fromName = res?.data[0]?.name
                ? res?.data[0]?.name + " sent you a message!"
                : "New Message!";

              showNotification(from, selectedUser, fromName, body);
            },
            () => {
              showNotification(from, selectedUser, "New Message!", body);
            }
          )
          );
        }
      }
    });

    await xmpp?.start().catch(console.warn);
    return xmpp;
  } catch (error) {
    console.error("Error initializing XMPP:", error);
    await removeXMPP();
    return null;
  }
};

const showNotification = (from, selectedUser, fromName, body) => {
  if (window?.location?.pathname?.includes("chat")) {
    if (!from?.includes(selectedUser?.receiver_uuid)) {
      notification.info({
        message: fromName,
        description: body,
        duration: 5,
      });
    }
  } else {
    notification.info({
      message: fromName,
      description: body,
      duration: 5,
    });
  }
};

export const fetchUsers = async () => {
  if (!xmpp) {
    console.warn("XMPP client is not initialized");
    return;
  }

  try {
    // Send an IQ stanza to retrieve the roster
    const iq = xml(
      "iq",
      { type: "get", id: "roster1" },
      xml("query", { xmlns: "jabber:iq:roster" })
    );

    const response = await xmpp?.sendReceive(iq);
    // console.log("USERS", response?.toString());

    const query = response.getChild("query", "jabber:iq:roster");

    if (!query) {
      console.warn("No roster data received");
      return [];
    }

    const items = query.getChildren("item");
    const roster = await Promise.all(
      items.map(async (item) => {
        const jid = item.attrs.jid;
        const name = item.attrs.name || jid; // Fallback to JID if no name is provided
        const subscription = item.attrs.subscription; // Subscription type (e.g., 'both', 'to', 'from', 'none')

        return {
          jid,
          name,
          subscription,
        };
      })
    );

    // console.log('Fetched roster with emails:', roster);
    return roster;
  } catch (error) {
    console.warn("Error fetching roster:", error);
    await timeout(5000);
    // fetchUsers();
    return [];
  }
};

export async function fetchChatHistory(withJid) {
  // console.log("Chat History of ", withJid);
  if (!xmpp) {
    console.warn("XMPP client is not initialized");
    return [];
  }

  const messages = [];
  let after; // Used for pagination
  let queryId = `query-${Date.now()}`; // Unique query ID
  let complete = false;

  while (!complete) {
    const iq = xml(
      "iq",
      { type: "set", id: queryId },
      xml(
        "query",
        { xmlns: "urn:xmpp:mam:2", queryid: queryId },
        xml(
          "x",
          { xmlns: "jabber:x:data", type: "submit" },
          xml(
            "field",
            { var: "FORM_TYPE", type: "hidden" },
            xml("value", {}, "urn:xmpp:mam:2")
          ),
          xml("field", { var: "with" }, xml("value", {}, withJid))
        ),
        after
          ? xml(
              "set",
              { xmlns: "http://jabber.org/protocol/rsm" },
              xml("after", {}, after)
            )
          : null
      )
    );

    try {
      const response = await xmpp?.sendReceive(iq);

      // Extract messages from the response
      const results = response?.getChildren("result", "urn:xmpp:mam:2") || [];
      results.forEach((result) => {
        try {
          const forwarded = result.getChild("forwarded", "urn:xmpp:forward:0");
          const delay = forwarded?.getChild("delay", "urn:xmpp:delay");
          const message = forwarded?.getChild("message", "jabber:client");

          if (message) {
            const timestamp = delay?.attrs?.stamp
              ? moment(delay.attrs.stamp).toISOString()
              : null;
            messages.push({
              from: message.attrs?.from || "unknown",
              to: message.attrs?.to || "unknown",
              body: message.getChildText("body") || "",
              timestamp: timestamp,
              isRead: true,
              messageTimeStamp: moment(timestamp).valueOf(),
              formattedDate: moment(timestamp).format("MMM DD, YYYY"),
              formattedTime: moment(timestamp).format("hh:mm a"),
            });
          }
        } catch (err) {
          console.warn("Error processing message result:", err);
        }
      });

      // Handle pagination and completion
      const fin = response?.getChild("fin", "urn:xmpp:mam:2");
      const rsmSet = fin?.getChild("set", "http://jabber.org/protocol/rsm");
      after = rsmSet?.getChildText("last") || undefined;
      complete = fin?.attrs?.complete === "true";
    } catch (error) {
      console.warn("Error fetching chat history:", error);
      break;
    }
  }

  // console.log("Fetched chat history:", messages);
  return messages;
}

export const removeXMPP = async (cb = () => {}) => {
  if (!xmpp) {
    return;
  }

  try {
    await xmpp?.stop();
    // console.log("XMPP client stopped successfully");
  } catch (error) {
    console.warn("Error stopping XMPP client:", error);
  } finally {
    xmpp = undefined;
    retryCount = 0;
    isXMPPConnected = false;
    cb();
  }
};

export const sendMessageToChat = async (
  inputMessage,
  recipientJID,
  sCall = () => {},
  eCall = () => {}
) => {
  // console.log("Sending Message", inputMessage);
  try {
    if (xmpp && inputMessage && recipientJID) {
      const message = xml(
        "message",
        { to: recipientJID, type: "chat" },
        xml("body", {}, inputMessage)
      );
      await xmpp?.send(message);
      sCall();
    }
  } catch (error) {
    console.warn("Error Sending Message", error);
    eCall(error);
  }
};
